import Dexie, { type Table } from 'dexie';
import type { ChangelogRecord } from '~~/layers/changelog/shared/types/changelog';

// Per-client version tracking in IndexedDB via Dexie. One db, one store, one row
// (id === 'state'). IndexedDB does not exist during SSR/prerender, so every access
// is `import.meta.client`-guarded and the db is constructed lazily on first use;
// the detection plugin that drives this is `.client` only.

class ChangelogDb extends Dexie {
	state!: Table<ChangelogRecord, string>;

	constructor() {
		super('lcp-changelog');
		this.version(1).stores({ state: 'id' });
	}
}

let db: ChangelogDb | null = null;

function getDb(): ChangelogDb {
	if (!db) db = new ChangelogDb();
	return db;
}

export function useChangelogDb() {
	async function read(): Promise<ChangelogRecord | undefined> {
		if (!import.meta.client) return undefined;
		try {
			return await getDb().state.get('state');
		} catch {
			// A blocked/absent IndexedDB (private mode, quota, disabled) must never
			// break the app; treat it as "no record" so detection seeds fresh.
			return undefined;
		}
	}

	async function write(record: ChangelogRecord): Promise<void> {
		if (!import.meta.client) return;
		try {
			await getDb().state.put(record);
		} catch {
			// Best-effort persistence: a failed write just means the popover may be
			// offered again next load, never a thrown error.
		}
	}

	return { read, write };
}
